import * as Api from '@ViewModels';
import { IAccount } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useAdminAccountFilterQuery } from '../../../../queries/Admin/Account/useAdminAccountFilterQuery';
import * as AdminApi from '../../../../viewmodels/AdminViewModels';
import {
	IMetaVisibleAsset,
	useMetaAssignManagedMutation,
	useMetaConnectionQuery,
} from '../../../../queries/Admin/Meta';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { getErrorMessage } from '../utils';
import { styleSheet } from './styles';

interface IProps {
	/** Meta ad account id to assign. */
	metaAdAccountId: string;
	/** Full visible-assets list, used to look up the display name of the ad account being assigned. */
	visibleAssets: IMetaVisibleAsset[];
	onClose: () => void;
	onSuccess: () => void;
}

export const MetaAssignModal: React.FC<IProps> = ({ metaAdAccountId, visibleAssets, onClose, onSuccess }) => {
	const [search, setSearch] = React.useState('');
	const [selectedAccount, setSelectedAccount] = React.useState<IAccount | null>(null);

	// The admin account-filter endpoint takes its narrowing predicates in the request
	// body via `criteria`. `autocompleteFragment` alone (a query-string param) doesn't
	// actually filter — you also have to push a Name criterion. Mirrors how
	// AdminAccountsPage builds its filter.
	const filter = React.useMemo<AdminApi.IAccountsFilterRequest>(() => {
		const trimmed = search.trim();
		const criteria: AdminApi.IAccountFilterCriteria[] = [];
		if (trimmed) {
			criteria.push({ property: AdminApi.AccountFilterCriteriaProperty.Name, value: trimmed });
		}
		return { criteria, op: Api.FilterOperator.And };
	}, [search]);

	const trimmedSearch = search.trim();
	const accountSearch = useAdminAccountFilterQuery({
		filter,
		pageSize: 25,
		autocompleteFragment: trimmedSearch || undefined,
		// Don't fire the search until the operator has typed something — otherwise
		// the modal opens with an unintended first-page-of-accounts dump and the
		// "Start typing…" placeholder below becomes unreachable.
		enabled: !!trimmedSearch,
	});

	const existingConnection = useMetaConnectionQuery({
		levitateAccountId: selectedAccount?.id,
	});

	const assignMutation = useMetaAssignManagedMutation();

	const target = visibleAssets.find(asset => asset.metaAdAccountId === metaAdAccountId);

	const handleConfirm = () => {
		if (!selectedAccount) return;
		const existingManaged = existingConnection.data?.managedAdAccountIds ?? [];
		const nextManaged = Array.from(new Set([...existingManaged, metaAdAccountId]));
		assignMutation.mutate(
			{ levitateAccountId: selectedAccount.id, metaAdAccountIds: nextManaged },
			{ onSuccess: () => onSuccess() }
		);
	};

	// Flatten every loaded page so the user actually sees all matches — anything
	// beyond the first page is otherwise invisible. Pagination beyond the first
	// page is driven by the Waypoint at the bottom of the result list below.
	const results = React.useMemo(
		() => accountSearch.data?.pages?.flatMap(page => page.values) ?? [],
		[accountSearch.data?.pages]
	);
	// `POST .../managed` REPLACES the customer's managed list. We MUST know their
	// current managed entries before submitting — otherwise the submit clobbers
	// every previously-managed ad account. Gate Confirm on the full loaded state
	// (including `isFetching`, which fires for background refetches that `isPending`
	// alone misses once the query has any prior result).
	const isLoadingExisting = !!selectedAccount && (existingConnection.isPending || existingConnection.isFetching);
	const existingFailedToLoad = !!selectedAccount && existingConnection.isError;
	const canSubmit =
		!!selectedAccount &&
		!assignMutation.isPending &&
		!existingConnection.isPending &&
		!existingConnection.isFetching &&
		!existingConnection.isError;

	return (
		<Dialog open={true} onOpenChange={open => !open && onClose()}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Assign ad account to a Levitate account</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styleSheet.body)}>
						<p className={css(styleSheet.intro)}>
							This ad account will be added to the chosen customer's managed list. Existing managed entries for that
							customer are preserved.
						</p>
						<div className={css(styleSheet.targetList)} aria-label='Selected ad account'>
							<div className={css(styleSheet.targetLine)}>
								{target ? <span className={css(styleSheet.targetName)}>{target.name}</span> : null}
								<span className={css(styleSheet.targetId)}>id: {metaAdAccountId}</span>
							</div>
						</div>
						<Input
							type='search'
							placeholder='Search Levitate accounts by name'
							value={search}
							// Don't auto-clear `selectedAccount` on every keystroke — typing one more
							// character after picking previously disabled Confirm. The user can change
							// the selection by clicking another result row.
							onChange={e => setSearch(e.target.value)}
							autoFocus={true}
						/>
						<div className={css(styleSheet.searchResults)}>
							{!trimmedSearch ? (
								<div className={css(styleSheet.emptyResults)}>Start typing to find a Levitate account.</div>
							) : accountSearch.isPending || accountSearch.isFetching ? (
								<div className={css(styleSheet.emptyResults)}>Searching…</div>
							) : results.length === 0 ? (
								<div className={css(styleSheet.emptyResults)}>No accounts match this search.</div>
							) : (
								<>
									{results.map(account => {
										const isSelected = selectedAccount?.id === account.id;
										return (
											<Button
												variant='link'
												key={account.id}
												onClick={() => setSelectedAccount(account)}
												styles={
													isSelected ? [styleSheet.resultRow, styleSheet.resultRowSelected] : [styleSheet.resultRow]
												}
											>
												<span className={css(styleSheet.resultName)}>{account.companyName || '(unnamed account)'}</span>
												<span className={css(styleSheet.resultMeta)}>id: {account.id}</span>
											</Button>
										);
									})}
									{accountSearch.hasNextPage ? (
										<Waypoint
											bottomOffset='-50px'
											onEnter={() => {
												if (!accountSearch.isFetchingNextPage) accountSearch.fetchNextPage();
											}}
										/>
									) : null}
									{accountSearch.isFetchingNextPage ? (
										<div className={css(styleSheet.emptyResults)}>Loading more…</div>
									) : null}
								</>
							)}
						</div>
						{existingFailedToLoad ? (
							<div className={css(styleSheet.errorBanner, styleSheet.errorBannerActionsRow)}>
								<div>
									{getErrorMessage(existingConnection.error) ||
										"Couldn't load this customer's current managed ad accounts. Assigning right now could overwrite their existing list."}
								</div>
								<Button
									variant='secondary'
									size='sm'
									onClick={() => existingConnection.refetch()}
									isLoading={existingConnection.isFetching}
								>
									Retry
								</Button>
							</div>
						) : null}
						{/*
						  Mutation failures surface as toasts via the explicit `onError` handler
						  in `useMetaAssignManagedMutation` — no inline banner here.
						*/}
					</div>
				</DialogBody>
				<DialogFooter>
					<DialogClose render={<Button variant='secondary' />}>Cancel</DialogClose>
					<Button
						variant='primary'
						onClick={handleConfirm}
						isLoading={assignMutation.isPending || isLoadingExisting}
						disabled={!canSubmit}
					>
						Confirm
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
